/**
 * Game: owns the main loop, stage lifecycle, run/profile state, and the flow
 * between title → stage → evolve transition → next stage → ending.
 */
import { Color, PerspectiveCamera, Scene, Vector3, type Camera, type Object3D } from 'three';
import { STAGE_LOADERS } from '@stages/registry';
import { Assets } from './assets';
import { EventBus } from './events';
import { Interpolator } from './interp';
import { Input } from './input';
import { resolveQuality, type QualityProfile } from './quality';
import { RenderSystem } from './render';
import { Profile, RunState } from './run';
import type { AudioApi, GameScreen, UiApi } from './services';
import type { DebugFlags, Stage, StageContext } from './stage';
import { TRAITS, type TraitDef } from './traits';
import { TREE, affinityShares, lineageOf, resolveBranch, type EvoNode, type StageId } from './tree';
import { disposeObject } from './utils/dispose';
import { Rng } from './utils/rng';

export const FIXED_DT = 1 / 60;
/** Seconds the player must hold Evolve. */
const EVOLVE_HOLD = 0.9;
/** Auto-evolve if the player sits at full growth this long. */
const AUTO_EVOLVE_AFTER = 50;

export interface AuxView {
  scene: Scene;
  camera: Camera;
  update?(dt: number): void;
}

export interface StartOptions {
  affinity?: Record<string, number>;
  traits?: string[];
  /** Skip the stage title card (QA). */
  skipCard?: boolean;
}

export class Game {
  readonly events = new EventBus();
  readonly profile = new Profile();
  readonly assets = new Assets();
  /** Render interpolation for objects registered with ctx.smooth(). */
  readonly interp = new Interpolator();
  readonly input: Input;
  readonly renderSystem: RenderSystem;
  readonly quality: QualityProfile;
  readonly debug: DebugFlags = { god: false, autopilot: false, verbose: false };
  ui!: UiApi;
  run: RunState = new RunState();
  screen: GameScreen = 'boot';
  timeScale = 1;

  stage?: Stage;
  private ctx?: StageContextImpl;
  private auxView?: AuxView;
  private blankScene = new Scene();
  private blankCamera = new PerspectiveCamera();
  private accumulator = 0;
  private lastFrame = 0;
  private evolveHold = 0;
  private fullGrowthTime = 0;
  private busy = false;
  private stageTraitsBefore: string[] = [];
  private affinityDirty = false;
  private affinityTimer = 0;
  private listenerDir = new Vector3();

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly audio: AudioApi,
    qualityOverride?: string | null,
  ) {
    this.quality = resolveQuality(this.profile.settings.quality, qualityOverride);
    this.renderSystem = new RenderSystem(canvas, this.quality);
    this.input = new Input(canvas);
    this.input.onPause = () => this.togglePause();
    this.blankScene.background = new Color('#000000');
    this.applySettings();
    window.addEventListener('resize', () => this.onResize());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.screen === 'playing') this.pause();
    });
  }

  /** Acquire the GPU device (WebGPU or WebGL2 fallback). Await before start(). */
  async init(): Promise<void> {
    await this.renderSystem.init();
  }

  /** Attach UI (constructed after Game because it needs a Game reference). */
  attachUi(ui: UiApi): void {
    this.ui = ui;
  }

  // -------------------------------------------------------------------------
  // Loop
  // -------------------------------------------------------------------------

  start(): void {
    this.lastFrame = performance.now();
    const loop = (now: number) => {
      requestAnimationFrame(loop);
      const frameDt = Math.min(0.1, Math.max(0, (now - this.lastFrame) / 1000));
      this.lastFrame = now;
      this.tick(frameDt);
    };
    requestAnimationFrame(loop);
  }

  private tick(frameDt: number): void {
    const stage = this.stage;
    const ctx = this.ctx;
    if (stage && ctx && this.screen === 'playing') {
      this.accumulator += frameDt * this.timeScale;
      const maxSteps = Math.ceil(6 * Math.max(1, this.timeScale));
      let steps = 0;
      while (this.accumulator >= FIXED_DT && steps < maxSteps) {
        this.interp.beforeStep();
        this.input.beginFrame();
        try {
          stage.update(FIXED_DT);
        } catch (err) {
          this.reportError(err);
        }
        ctx.time += FIXED_DT;
        this.run.data.playTime += FIXED_DT;
        this.updateEvolve(FIXED_DT);
        this.input.endFrame();
        this.accumulator -= FIXED_DT;
        steps++;
      }
      if (steps >= maxSteps) this.accumulator = 0;
      this.affinityTimer -= frameDt;
      if (this.affinityDirty && this.affinityTimer <= 0) this.flushAffinity();
    } else {
      this.accumulator = 0;
    }
    // frame() runs whenever a stage is active — including behind the title card (before
    // start()), while paused, and during the devolve overlay — so the world stays alive.
    this.input.beginRender();
    // Render smoothed poses (between the last two fixed steps) so motion and cameras don't judder.
    const interpolating = !!(stage && ctx && this.screen === 'playing');
    if (interpolating) this.interp.apply(this.accumulator / FIXED_DT);
    if (stage && ctx) {
      try {
        stage.frame(frameDt, this.accumulator / FIXED_DT);
      } catch (err) {
        this.reportError(err);
      }
    }
    this.input.endRender();
    if (this.auxView) {
      this.auxView.update?.(frameDt);
      this.renderSystem.setView(this.auxView.scene, this.auxView.camera);
    } else if (stage && ctx) {
      this.renderSystem.setView(ctx.scene, ctx.camera);
    } else {
      this.renderSystem.setView(this.blankScene, this.blankCamera);
    }
    if (ctx && !this.auxView) {
      this.audio.setListener(ctx.camera.position, ctx.camera.getWorldDirection(this.listenerDir));
    }
    this.renderSystem.render(frameDt);
    if (interpolating) this.interp.restore();
    this.ui?.setFps(this.renderSystem.fps);
  }

  /** UI can render its own 3D scene (title background, transitions) instead of the stage. */
  setAuxView(view: AuxView | null): void {
    this.auxView = view ?? undefined;
    if (view && view.camera instanceof PerspectiveCamera) {
      const { width, height } = this.renderSystem.size;
      view.camera.aspect = width / height;
      view.camera.updateProjectionMatrix();
    }
  }

  private onResize(): void {
    this.renderSystem.resize();
    const { width, height } = this.renderSystem.size;
    for (const cam of [this.ctx?.camera, this.auxView?.camera]) {
      if (cam instanceof PerspectiveCamera) {
        cam.aspect = width / height;
        cam.updateProjectionMatrix();
      }
    }
  }

  private setScreen(screen: GameScreen): void {
    this.screen = screen;
    this.input.enabled = screen === 'playing';
    if (screen !== 'playing') this.input.exitPointerLock();
    this.events.emit('screen', { screen });
  }

  // -------------------------------------------------------------------------
  // Public flow API (used by UI + debug hooks)
  // -------------------------------------------------------------------------

  get node(): EvoNode | undefined {
    return this.stage ? TREE[this.run.stage] : undefined;
  }

  hasSave(): boolean {
    return !!RunState.load();
  }

  showTitle(): void {
    this.teardownStage();
    this.setScreen('title');
    this.audio.music('menu');
    this.ui.showTitle({ canContinue: this.hasSave() });
  }

  /** Start a fresh run at the root (or at `start` for codex replays / QA). */
  async newRun(start: StageId = 'cell', opts: StartOptions = {}): Promise<void> {
    const lineage = lineageOf(start);
    this.run = new RunState({ stage: start, history: lineage, affinity: {} });
    // Starting mid-tree (codex/QA): grant the first trait of each ancestor for balance.
    for (const s of lineage.slice(0, -1)) {
      const first = Object.values(TRAITS).find((t) => t.from === s);
      if (first) this.run.grantTrait(first.id);
    }
    for (const t of opts.traits ?? []) this.run.grantTrait(t);
    this.profile.data.runsStarted++;
    this.profile.discover(start);
    this.run.save();
    await this.loadAndStart(start, opts);
  }

  async continueRun(): Promise<void> {
    const saved = RunState.load();
    if (!saved) return this.newRun();
    this.run = saved;
    this.run.data.affinity = {};
    await this.loadAndStart(saved.stage, {});
  }

  pause(): void {
    if (this.screen !== 'playing') return;
    this.setScreen('paused');
    this.input.releaseAll();
    this.ui.showPause();
  }

  resume(): void {
    if (this.screen !== 'paused') return;
    this.ui.hidePause();
    this.setScreen('playing');
  }

  togglePause(): void {
    if (this.screen === 'playing') this.pause();
    else if (this.screen === 'paused') this.resume();
  }

  /** Restart the current stage from scratch. */
  async restartStage(): Promise<void> {
    if (!this.stage) return;
    this.ui.hidePause();
    this.run.data.traits = [...this.stageTraitsBefore];
    await this.loadAndStart(this.run.stage, {});
  }

  quitToTitle(): void {
    this.ui.hidePause();
    this.run.save();
    this.showTitle();
  }

  applySettings(): void {
    const s = this.profile.settings;
    this.audio.setVolumes({ master: s.masterVolume, music: s.musicVolume, sfx: s.sfxVolume, voice: s.voiceVolume });
    this.input.sensitivity = s.cameraSensitivity;
    this.input.invertY = s.invertY;
    this.ui?.showFps(s.showFps);
  }

  // -------------------------------------------------------------------------
  // Stage lifecycle
  // -------------------------------------------------------------------------

  private teardownStage(): void {
    if (this.stage) {
      try {
        this.stage.dispose();
      } catch (err) {
        this.reportError(err);
      }
    }
    if (this.ctx) {
      this.interp.clear();
      disposeObject(this.ctx.scene);
      this.ctx.scene.clear();
      void this.assets.purge();
    }
    this.stage = undefined;
    this.ctx = undefined;
    this.ui?.hud.show(false);
    this.ui?.hud.bossBar(null);
    this.ui?.hud.evolvePrompt({ visible: false });
    this.ui?.narrator.clear();
  }

  /** Build (but don't start) a stage. */
  private async prepare(id: StageId, opts: StartOptions): Promise<{ stage: Stage; ctx: StageContextImpl }> {
    this.events.emit('stage:loading', { stage: id });
    this.renderSystem.reset();
    let mod;
    try {
      mod = await STAGE_LOADERS[id]();
    } catch (err) {
      // A new deploy removed this build's hashed chunks (stale tab) → reload onto the new build
      // and resume the saved run instead of dumping the player on the title (#135).
      if (reloadForNewBuild(this.run)) return new Promise<never>(() => undefined);
      throw err;
    }
    const ctx = new StageContextImpl(this, id);
    if (opts.affinity) for (const [k, v] of Object.entries(opts.affinity)) this.run.addAffinity(k, v);
    const stage = new mod.default(ctx);
    ctx.loading = true;
    await stage.load();
    ctx.loading = false;
    return { stage, ctx };
  }

  private async loadAndStart(id: StageId, opts: StartOptions): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.teardownStage();
      this.ui.hideTitle();
      this.setScreen('loading');
      this.ui.showLoading(TREE[id]);
      this.run.enterStage(id);
      this.stageTraitsBefore = [...this.run.data.traits];
      const prepared = await this.prepare(id, opts);
      this.ui.hideLoading();
      await this.activate(prepared, opts);
    } catch (err) {
      this.reportError(err);
      this.ui.hideLoading();
      this.showTitle();
    } finally {
      this.busy = false;
    }
  }

  private async activate(p: { stage: Stage; ctx: StageContextImpl }, opts: StartOptions): Promise<void> {
    const node = TREE[p.ctx.id];
    this.stage = p.stage;
    this.ctx = p.ctx;
    this.evolveHold = 0;
    this.fullGrowthTime = 0;
    this.onResize();
    this.profile.discover(node.id);
    const hud = this.ui.hud;
    hud.setActions(node.actions);
    hud.setGrowth(p.ctx.growth);
    hud.setAffinity(node.paths, affinityShares(node.id, this.run.data.affinity));
    hud.setObjective(null);
    hud.evolvePrompt({ visible: false });
    this.ui.setTouchControls(this.quality.isTouch);
    this.audio.music(node.id);
    this.audio.setIntensity(0.2);
    // Render the stage behind the title card, then begin.
    this.setScreen('loading');
    if (!opts.skipCard && !this.debug.autopilot) await this.ui.showStageCard(node);
    hud.show(true);
    this.setScreen('playing');
    this.events.emit('stage:start', { stage: node.id });
    try {
      p.stage.start();
      if (this.debug.autopilot) p.stage.setAutopilot(true, this.debug.preferPath);
    } catch (err) {
      this.reportError(err);
    }
  }

  // -------------------------------------------------------------------------
  // Progress, evolution, death, endings
  // -------------------------------------------------------------------------

  /** @internal */ onGrowth(ctx: StageContextImpl, prev: number): void {
    const node = TREE[ctx.id];
    this.ui.hud.setGrowth(ctx.growth);
    this.events.emit('stage:growth', { stage: ctx.id, growth: ctx.growth });
    if (prev < 1 && ctx.growth >= 1 && node.kind === 'stage') {
      const path = resolveBranch(node.id, this.run.data.affinity);
      this.audio.sfx('evolve_ready');
      this.events.emit('stage:evolve-ready', { stage: node.id, to: path?.to });
      void this.ui.narrator.sayOneOf(
        [
          'Look at you, all grown up. Hold Evolve whenever you are ready.',
          "You're ready to evolve. Or keep eating. I'm not your mom.",
          'Evolution ready! Keep playing to push your mutation, or hold Evolve now.',
        ],
        { mood: 'excited', priority: 2 },
      );
    }
  }

  /** @internal */ markAffinityDirty(): void {
    this.affinityDirty = true;
  }

  private flushAffinity(): void {
    this.affinityDirty = false;
    this.affinityTimer = 0.1;
    const node = this.node;
    if (node) this.ui.hud.setAffinity(node.paths, affinityShares(node.id, this.run.data.affinity));
  }

  private updateEvolve(dt: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const node = TREE[ctx.id];
    if (node.kind !== 'stage' || ctx.growth < 1) {
      if (this.evolveHold !== 0 || this.fullGrowthTime !== 0) {
        this.evolveHold = 0;
        this.fullGrowthTime = 0;
        this.ui.hud.evolvePrompt({ visible: false });
      }
      return;
    }
    this.fullGrowthTime += dt;
    const path = resolveBranch(node.id, this.run.data.affinity)!;
    if (this.input.down('interact')) this.evolveHold += dt;
    else this.evolveHold = Math.max(0, this.evolveHold - dt * 2);
    this.ui.hud.evolvePrompt({ visible: true, target: TREE[path.to], path, holdFrac: this.evolveHold / EVOLVE_HOLD });
    const auto = this.debug.autopilot ? 1.5 : AUTO_EVOLVE_AFTER;
    if (this.evolveHold >= EVOLVE_HOLD || this.fullGrowthTime >= auto) void this.evolve();
  }

  /** Evolve along the leading branch (or a forced one). */
  async evolve(pathKey?: string): Promise<void> {
    const stage = this.stage;
    const ctx = this.ctx;
    if (!stage || !ctx || this.busy) return;
    const from = TREE[ctx.id];
    if (from.kind !== 'stage') return this.finishRun();
    this.busy = true;
    try {
      const path = (pathKey && from.paths.find((p) => p.key === pathKey)) || resolveBranch(from.id, this.run.data.affinity)!;
      const to = TREE[path.to];
      this.setScreen('transition');
      this.ui.hud.evolvePrompt({ visible: false });
      this.audio.sfx('evolve');
      this.events.emit('stage:complete', { stage: from.id, to: to.id });
      try {
        await stage.outro();
      } catch (err) {
        this.reportError(err);
      }
      const snapshot = this.renderSystem.snapshot();
      const traitsGained: TraitDef[] = this.run.data.traits
        .filter((t) => !this.stageTraitsBefore.includes(t))
        .map((t) => TRAITS[t])
        .filter(Boolean);
      this.teardownStage();
      this.run.enterStage(to.id);
      this.stageTraitsBefore = [...this.run.data.traits];
      this.run.save();
      this.profile.discover(to.id);
      const ready = this.prepare(to.id, {});
      ready.catch(() => undefined);
      this.audio.music('transition');
      await this.ui.playTransition({ from, to, path, traitsGained, snapshot }, ready.then(() => undefined));
      const prepared = await ready;
      await this.activate(prepared, {});
    } catch (err) {
      this.reportError(err);
      this.showTitle();
    } finally {
      this.busy = false;
    }
  }

  /** @internal */ async onPlayerDied(cause?: string): Promise<void> {
    const ctx = this.ctx;
    const stage = this.stage;
    if (!ctx || !stage || this.screen !== 'playing' || this.debug.god) return;
    this.setScreen('devolving');
    this.run.data.devolutions++;
    this.audio.sfx('death');
    this.events.emit('player:death', { stage: ctx.id, cause });
    this.ui.hud.evolvePrompt({ visible: false });
    await this.ui.showDevolve({ node: TREE[ctx.id], cause, devolutions: this.run.data.devolutions });
    if (this.stage !== stage) return;
    let r: void | 'reload' = 'reload';
    try {
      r = stage.respawn();
      this.interp.teleportAll();
    } catch (err) {
      this.reportError(err);
    }
    if (r === 'reload') {
      this.run.data.traits = [...this.stageTraitsBefore];
      await this.loadAndStart(ctx.id, { skipCard: true });
    } else {
      this.setScreen('playing');
    }
    this.events.emit('player:respawn', { stage: ctx.id });
  }

  /** Terminal/final stage finished → ending screen. */
  async finishRun(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const node = TREE[ctx.id];
    this.setScreen('ending');
    this.ui.hud.show(false);
    this.profile.unlockEnding(node.id, this.run.data.playTime);
    this.events.emit('ending', { stage: node.id });
    this.audio.sfx('ending');
    this.audio.music('ending');
    const info = {
      node,
      history: [...this.run.data.history],
      playTime: this.run.data.playTime,
      devolutions: this.run.data.devolutions,
      traits: this.run.data.traits.map((t) => TRAITS[t]).filter(Boolean),
      endingsFound: [...this.profile.data.endings],
    };
    RunState.clear();
    this.ui.showEnding(info);
  }

  /** @internal */ onComplete(ctx: StageContextImpl, pathKey?: string): void {
    if (ctx !== this.ctx || this.screen !== 'playing') return;
    const node = TREE[ctx.id];
    if (node.kind === 'stage') void this.evolve(pathKey);
    else void this.finishRun();
  }

  /** @internal */ onTrait(id: string): void {
    const t = TRAITS[id];
    if (!t) return;
    this.ui.hud.toast(`New trait: ${t.name} — ${t.description}`, 'trait');
    this.audio.sfx('trait');
    this.events.emit('trait', { id });
  }

  reportError(err: unknown): void {
    const message = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : String(err);
    console.error('[game]', err);
    this.events.emit('error', { message });
  }
}

/** Concrete StageContext handed to stages. */
class StageContextImpl implements StageContext {
  readonly node: EvoNode;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly rng: Rng;
  time = 0;
  growth = 0;
  loading = false;

  constructor(
    private readonly game: Game,
    readonly id: StageId,
  ) {
    this.node = TREE[id];
    const { width, height } = game.renderSystem.size;
    this.camera = new PerspectiveCamera(55, width / Math.max(1, height), 0.1, 2000);
    this.rng = new Rng((game.run.data.seed ^ (id.length * 7919 + id.charCodeAt(0) * 104729)) >>> 0);
  }

  get renderer() {
    return this.game.renderSystem.renderer;
  }
  get input() {
    return this.game.input;
  }
  get audio() {
    return this.game.audio;
  }
  get hud() {
    return this.game.ui.hud;
  }
  get narrator() {
    return this.game.ui.narrator;
  }
  get post() {
    return this.game.renderSystem;
  }
  get quality() {
    return this.game.quality;
  }
  get settings() {
    return this.game.profile.settings;
  }
  get run() {
    return this.game.run;
  }
  get assets() {
    return this.game.assets;
  }
  get events() {
    return this.game.events;
  }
  get debug() {
    return this.game.debug;
  }

  smooth(obj: Object3D, opts?: { rotation?: boolean }): void {
    this.game.interp.add(obj, opts);
  }

  unsmooth(obj: Object3D): void {
    this.game.interp.remove(obj);
  }

  teleport(obj: Object3D): void {
    this.game.interp.teleport(obj);
  }

  reportProgress(frac: number): void {
    this.game.ui.setLoadingProgress(Math.max(0, Math.min(1, frac)));
  }

  setGrowth(frac: number): void {
    const prev = this.growth;
    this.growth = Math.max(0, Math.min(1, frac));
    if (this.growth !== prev && !this.loading) this.game.onGrowth(this, prev);
  }

  addAffinity(key: string, amount: number): void {
    if (!this.node.paths.some((p) => p.key === key)) return;
    this.game.run.addAffinity(key, amount);
    this.game.markAffinityDirty();
  }

  grantTrait(id: string): void {
    if (this.game.run.grantTrait(id)) this.game.onTrait(id);
  }

  playerDied(cause?: string): void {
    void this.game.onPlayerDied(cause);
  }

  complete(pathKey?: string): void {
    this.game.onComplete(this, pathKey);
  }

  shakeAllowed(): boolean {
    return this.game.profile.settings.screenShake && !this.game.profile.settings.reducedMotion;
  }
}

const RELOAD_FLAG = 'evo.chunkReload';

/** Save the run and reload once (per 30 s) when a code chunk fails to load after a deploy. */
export function reloadForNewBuild(run?: RunState): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < 30_000) return false; // already tried — don't loop
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    run?.save();
    const u = new URL(location.href);
    for (const k of ['stage', 'skipcard', 'intro']) u.searchParams.delete(k);
    u.searchParams.set('resume', '1');
    location.replace(u.toString());
    return true;
  } catch {
    return false;
  }
}
